// src/components/Admin/ChatManager/Chat/MessageLogTable.jsx
import { useEffect, useState, useCallback, memo } from 'react';
import {
    Table, TableHead, TableRow, TableCell, TableBody,
    Typography, Paper, Box, Pagination, useMediaQuery,
    IconButton, Tooltip, Button, Stack, Chip, Divider,
    Collapse,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import api from '../../../../api/axios';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import dayjs from 'dayjs';

const PAGE_SIZE = 20;
const EXPORT_LIMIT = 5000;

export default function MessageLogTable({
    categoryId,
    categoryName = '',
    keyword,
    startDate,
    endDate,
}) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [logs, setLogs] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);

    const loadLogs = useCallback(() => {
        const isAll = categoryId === 'all';
        const url = isAll
            ? '/api/chat/logs/all/'
            : `/api/chat/categories/${categoryId}/logs/`;

        const params = {
            page,
            page_size: PAGE_SIZE,
            keyword,
            start_date: startDate,
            end_date: endDate,
        };

        api.get(url, { params })
            .then((res) => {
                const results = res.data.results || res.data.logs || [];
                setLogs(results);
                setTotalCount(res.data.count || results.length);
                setLoading(false);
            })
            .catch(() => {
                alert('메시지 로그를 불러오지 못했습니다.');
                setLoading(false);
            });
    }, [categoryId, keyword, startDate, endDate, page]);

    useEffect(() => {
        setLoading(true);
        loadLogs();
    }, [loadLogs]);

    const toggleBlind = (id, blind) => {
        const url = `/api/chat/messages/${id}/${blind ? 'blind' : 'unblind'}/`;
        const txt = blind ? '블라인드 처리' : '블라인드 해제';
        if (!window.confirm(`${txt} 하시겠습니까?`)) return;

        api.post(url)
            .then(() => {
                alert(`${txt} 완료`);
                setLogs((prev) => prev.map((m) => (m.id === id ? { ...m, is_blinded: blind } : m)));
            })
            .catch(() => alert(`${txt} 실패`));
    };

    const exportToExcel = useCallback(() => {
        const isAll = categoryId === 'all';
        const url = isAll
            ? '/api/chat/logs/all/'
            : `/api/chat/categories/${categoryId}/logs/`;

        api.get(url, {
            params: {
                page: 1,
                page_size: EXPORT_LIMIT,
                keyword,
                start_date: startDate,
                end_date: endDate,
            },
        })
            .then((res) => {
                const data = res.data.results || res.data.logs || [];

                const rows = data.map((m) => [
                    m.category || categoryName || '',
                    m.is_anonymous_chat
                        ? `${m.sender_username || '익명'} (${m.display_name || ''})`
                        : (m.display_name || m.sender_username || ''),
                    m.content,
                    new Date(m.created_at).toLocaleString('ko-KR'),
                    m.is_blinded ? 'Y' : 'N',
                ]);

                const wb = XLSX.utils.book_new();
                const ws = XLSX.utils.aoa_to_sheet([
                    ['채팅방', '닉네임', '내용', '시간', '블라인드'],
                    ...rows,
                ]);
                ws['!cols'] = [{ wch: 18 }, { wch: 20 }, { wch: 60 }, { wch: 22 }, { wch: 10 }];
                XLSX.utils.book_append_sheet(wb, ws, 'Logs');

                const blob = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
                saveAs(new Blob([blob], { type: 'application/octet-stream' }), `chat_logs_${Date.now()}.xlsx`);
            })
            .catch(() => alert('엑셀 다운로드에 실패했습니다.'));
    }, [categoryId, categoryName, keyword, startDate, endDate]);

    useEffect(() => {
        const handler = () => exportToExcel();
        window.addEventListener('exportToExcel', handler);
        return () => window.removeEventListener('exportToExcel', handler);
    }, [exportToExcel]);

    if (loading) return <Typography sx={{ p: 2 }}>불러오는 중...</Typography>;

    const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

    return (
        <Paper
            sx={{
                p: 2,
                width: '100%',
                maxWidth: '100%',
                minWidth: 0,
                overflowX: 'clip', // ✅ 문서 가로스크롤 차단(모바일에서 특히 안정적)
            }}
        >
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                <Typography variant="h6">메시지 로그</Typography>

                {/* (PC에서만) 빠른 엑셀 버튼을 여기 넣고 싶으면 활성화 */}
                {/* {!isMobile && (
          <Button variant="outlined" size="small" onClick={exportToExcel}>
            엑셀
          </Button>
        )} */}
            </Stack>

            {/* ✅ 모바일: 카드 / PC: 테이블 */}
            {isMobile ? (
                <MobileCards
                    logs={logs}
                    categoryName={categoryName}
                    toggleBlind={toggleBlind}
                />
            ) : (
                <Table
                    size="small"
                    sx={{
                        width: '100%',
                        minWidth: 0,
                        tableLayout: 'fixed',
                    }}
                >
                    <TableHead>
                        <TableRow>
                            <TableCell
                                align="center"
                                sx={{
                                    width: 160,
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                채팅방
                            </TableCell>

                            <TableCell
                                align="center"
                                sx={{
                                    width: 220,
                                    minWidth: 0,
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                }}
                            >
                                닉네임
                            </TableCell>

                            <TableCell align="left">내용</TableCell>

                            <TableCell
                                align="center"
                                sx={{
                                    width: 170,
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                시간
                            </TableCell>

                            <TableCell
                                align="center"
                                sx={{
                                    width: 110,
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                관리
                            </TableCell>
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {logs.map((m) => {
                            const room = m.category || categoryName || '알 수 없음';
                            const nick = m.sender_username || '알 수 없음';
                            const disp = m.is_anonymous_chat
                                ? `${nick} (${m.display_name || ''})`
                                : (m.display_name || nick);

                            const dateStr = dayjs(m.created_at).format('YYYY-MM-DD');
                            const timeStr = dayjs(m.created_at).format('A hh:mm:ss');

                            return (
                                <TableRow
                                    key={m.id}
                                    sx={{
                                        ...(m.is_blinded && { bgcolor: '#f7f7f7' }),
                                        '& td': {
                                            px: 1,
                                            py: 0.75,
                                            minWidth: 0,
                                        },
                                    }}
                                >
                                    <TableCell align="center" sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                        {room}
                                    </TableCell>

                                    <TableCell
                                        align="center"
                                        sx={{
                                            minWidth: 0,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        {disp}
                                    </TableCell>

                                    <TableCell
                                        align="left"
                                        sx={{
                                            minWidth: 0,
                                            whiteSpace: 'normal',
                                            wordBreak: 'break-word',
                                            overflowWrap: 'anywhere',
                                        }}
                                    >
                                        {m.is_blinded ? (
                                            <i style={{ color: 'gray' }}>숨겨진 메시지</i>
                                        ) : (
                                            m.content
                                        )}
                                    </TableCell>

                                    <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                                        {dateStr} {timeStr}
                                    </TableCell>

                                    <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                                        {m.is_blinded ? (
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                onClick={() => toggleBlind(m.id, false)}
                                            >
                                                해제
                                            </Button>
                                        ) : (
                                            <Button
                                                variant="outlined"
                                                color="error"
                                                size="small"
                                                onClick={() => toggleBlind(m.id, true)}
                                            >
                                                블라인드
                                            </Button>
                                        )}
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            )}

            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                <Pagination
                    count={totalPages}
                    page={page}
                    onChange={(_, v) => setPage(v)}
                    shape="rounded"
                    color="primary"
                    size={isMobile ? 'small' : 'medium'}
                />
            </Box>
        </Paper>
    );
}

/* ─────────────────────────────────────────────
   Mobile Cards (운영자용 카드 UI)
   - 상태 Chip
   - 내용 2줄 미리보기 + 더보기(펼치기)
   - 닉네임/채팅방/시간 분리
   - 복사 버튼(관리 편의)
   - 블라인드 토글 아이콘(원터치)
───────────────────────────────────────────── */
const MobileCards = memo(function MobileCards({ logs, categoryName, toggleBlind }) {
    const [openMap, setOpenMap] = useState({}); // { [id]: true/false }

    const toggleOpen = (id) => {
        setOpenMap((prev) => ({ ...prev, [id]: !prev[id] }));
    };

    const copyText = async (text) => {
        try {
            await navigator.clipboard.writeText(text || '');
            alert('복사되었습니다.');
        } catch {
            alert('복사에 실패했습니다.');
        }
    };

    return (
        <Stack spacing={1}>
            {logs.map((m) => {
                const room = m.category || categoryName || '알 수 없음';
                const nick = m.sender_username || '알 수 없음';
                const disp = m.is_anonymous_chat
                    ? `${nick} (${m.display_name || ''})`
                    : (m.display_name || nick);

                const dateStr = dayjs(m.created_at).format('YYYY-MM-DD');
                const timeStr = dayjs(m.created_at).format('A hh:mm:ss');

                const opened = !!openMap[m.id];
                const visibleText = m.is_blinded ? '숨겨진 메시지' : (m.content || '');

                return (
                    <Paper
                        key={m.id}
                        variant="outlined"
                        sx={{
                            p: 1.25,
                            borderRadius: 2,
                            minWidth: 0,
                            overflow: 'hidden',
                            ...(m.is_blinded && { bgcolor: '#f7f7f7' }),
                            borderColor: m.is_blinded ? 'grey.300' : 'grey.200',
                        }}
                    >
                        {/* 헤더: 닉네임 + 상태칩 + 관리아이콘 */}
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                justifyContent: 'space-between',
                                gap: 1,
                                minWidth: 0,
                            }}
                        >
                            <Box sx={{ minWidth: 0 }}>
                                <Typography
                                    variant="body2"
                                    fontWeight={900}
                                    sx={{
                                        fontSize: '0.95rem',
                                        lineHeight: 1.25,
                                        wordBreak: 'break-word',
                                        overflowWrap: 'anywhere',
                                    }}
                                >
                                    {disp}
                                </Typography>

                                <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 0.25, flexWrap: 'wrap' }}>
                                    <Typography variant="caption" color="text.secondary">
                                        {room}
                                    </Typography>

                                    <Chip
                                        size="small"
                                        label={m.is_blinded ? '블라인드' : '정상'}
                                        variant={m.is_blinded ? 'filled' : 'outlined'}
                                        sx={{
                                            height: 20,
                                            fontSize: '0.7rem',
                                            fontWeight: 800,
                                        }}
                                    />

                                    <Typography variant="caption" color="text.secondary">
                                        {dateStr} {timeStr}
                                    </Typography>
                                </Stack>
                            </Box>

                            <Stack direction="row" spacing={0.25} alignItems="center" sx={{ flex: 'none' }}>
                                <Tooltip title="복사">
                                    <IconButton
                                        size="small"
                                        onClick={() => copyText(visibleText)}
                                        sx={{ p: 0.5 }}
                                    >
                                        <ContentCopyIcon sx={{ fontSize: 18 }} />
                                    </IconButton>
                                </Tooltip>

                                <Tooltip title={m.is_blinded ? '숨김 해제' : '블라인드'}>
                                    <IconButton
                                        size="small"
                                        color={m.is_blinded ? 'primary' : 'error'}
                                        onClick={() => toggleBlind(m.id, !m.is_blinded)}
                                        sx={{ p: 0.5 }}
                                    >
                                        {m.is_blinded ? (
                                            <VisibilityIcon sx={{ fontSize: 18 }} />
                                        ) : (
                                            <VisibilityOffIcon sx={{ fontSize: 18 }} />
                                        )}
                                    </IconButton>
                                </Tooltip>
                            </Stack>
                        </Box>

                        <Divider sx={{ my: 1 }} />

                        {/* 본문: 기본 2줄 미리보기 + 더보기 */}
                        <Box sx={{ minWidth: 0 }}>
                            {!opened ? (
                                <Typography
                                    variant="body2"
                                    sx={{
                                        fontSize: '0.9rem',
                                        color: m.is_blinded ? 'text.secondary' : 'text.primary',
                                        display: '-webkit-box',
                                        WebkitLineClamp: 2,
                                        WebkitBoxOrient: 'vertical',
                                        overflow: 'hidden',
                                        wordBreak: 'break-word',
                                        overflowWrap: 'anywhere',
                                    }}
                                >
                                    {m.is_blinded ? <i>숨겨진 메시지</i> : (m.content || '')}
                                </Typography>
                            ) : (
                                <Collapse in={opened} timeout="auto" unmountOnExit={false}>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontSize: '0.9rem',
                                            color: m.is_blinded ? 'text.secondary' : 'text.primary',
                                            whiteSpace: 'normal',
                                            wordBreak: 'break-word',
                                            overflowWrap: 'anywhere',
                                        }}
                                    >
                                        {m.is_blinded ? <i>숨겨진 메시지</i> : (m.content || '')}
                                    </Typography>
                                </Collapse>
                            )}

                            <Box sx={{ mt: 0.75, display: 'flex', justifyContent: 'flex-end' }}>
                                <Button
                                    size="small"
                                    variant="text"
                                    onClick={() => toggleOpen(m.id)}
                                    endIcon={
                                        <ExpandMoreIcon
                                            sx={{
                                                transform: opened ? 'rotate(180deg)' : 'rotate(0deg)',
                                                transition: 'transform 0.15s ease',
                                            }}
                                        />
                                    }
                                    sx={{
                                        fontSize: '0.75rem',
                                        px: 1,
                                        minWidth: 0,
                                    }}
                                >
                                    {opened ? '접기' : '더보기'}
                                </Button>
                            </Box>
                        </Box>
                    </Paper>
                );
            })}

            {logs.length === 0 && (
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, textAlign: 'center' }}>
                    <Typography color="text.secondary">데이터가 없습니다.</Typography>
                </Paper>
            )}
        </Stack>
    );
});
